import { useGetAllTabsUserHasAccessToQuery, useGetIfUserIsAdminQuery } from "../redux/api/user";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton, theme } from "antd";
import CustomIcons from "../components/UI/customIcons/CustomIcons";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  HighlightOutlined,
  FileDoneOutlined,
  SoundOutlined,
  ShopOutlined,
  DollarOutlined,
  MoneyCollectOutlined,
  CalculatorOutlined,
  FacebookOutlined,
  UserOutlined,
  RobotOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { useSideNavOfflinePlatforms } from "./useSideNavOfflinePlatforms";
import { LayoutOutlined, HistoryOutlined, BranchesOutlined, CompassOutlined } from "@ant-design/icons";

export default function SideNavDynamicItems() {
  const location = useLocation();
  const currentUrlPath = location.pathname;
  const collapsed = useSelector((state) => state.sider.collapsed);
  const isAuthenticated = useSelector((state) => state.oauth.isAuthenticated);
  const { token } = theme.useToken();
  const navigate = useNavigate();

  // Query for allowed tabs
  const allowedTabs = useGetAllTabsUserHasAccessToQuery(undefined, {
    skip: !isAuthenticated,
  });
  const tabsData = useMemo(() => allowedTabs.data || [], [allowedTabs.data]);
  const offlineItems = useSideNavOfflinePlatforms();
  const { data: isAdmin } = useGetIfUserIsAdminQuery(undefined, { skip: !isAuthenticated });

  // Internal loading state - always starts as true
  const [isLoading, setIsLoading] = useState(true);

  // Handle minimum loading time exactly like in original code
  useEffect(() => {
    const minLoadingTimer = setTimeout(() => {
      if (
        allowedTabs.isLoading === false &&
        allowedTabs.isSuccess &&
        allowedTabs.isError === false
      ) {
        setIsLoading(false);
      }
    }, 1000); // Minimum 1 second display

    return () => clearTimeout(minLoadingTimer);
  }, [allowedTabs.isLoading, allowedTabs.isSuccess, allowedTabs.isError]);

  // Convert array to Set for faster lookups
  const allowedTabsSet = useMemo(() => new Set(tabsData), [tabsData]);

  // Create menu items with proper enabled/disabled state
  const getMenuItemForTab = useCallback(
    (tab) => {
      // Check if the tab is in allowed tabs
      const isAllowed = allowedTabsSet.has(tab);

      // Special case for settings which is always allowed
      const isSettingsTab = tab === "legacy_settings";
      const isSettingsLandingTab = tab === "settings_landing";

      const baseStyles = { animation: "fadeIn 1s" };

      // By default, all items are disabled
      // They are only enabled if they are in allowedTabs AND loading is complete
      const commonProps = {
        // Enable item only if it's allowed or it's the settings tab
        disabled: !(isAllowed || isSettingsTab || isSettingsLandingTab) && !isLoading,
        style: baseStyles,
      };

      switch (tab) {
        case "executive_summary":
          return {
            key: "/executive-summary",
            label: "Executive Summary",
            onClick: () => navigate("/executive-summary"),
            ...commonProps,
          };

        case "customer_performance":
          return {
            key: "/performance/customer-performance",
            label: "Customer Performance",
            onClick: () => navigate("/performance/customer-performance"),
            icon: (
              <div style={{ marginRight: "1px", marginTop: "5px" }}>
                <CustomIcons
                  icon="customer_performance"
                  height={20}
                  width={20}
                />
              </div>
            ),
            ...commonProps,
          };

        case "site_analytics":
          return {
            key: "/site-analytics",
            label: "Site Analytics",
            onClick: () => navigate("/site-analytics"),
            ...commonProps,
          };

        case "compass":
          return {
            key: "/compass",
            label: "Compass",
            onClick: () => navigate("/compass"),
            ...commonProps,
          };

        case "compass_portal":
          return {
            key: "/compass-portal",
            label: "Compass Portal",
            icon: <CompassOutlined />,
            onClick: () => navigate("/compass-portal"),
            ...commonProps,
          };

        case "ltr":
          return {
            key: "/customer-analytics",
            label: "Customer Analytics",
            onClick: () => navigate("/customer-analytics"),
            ...commonProps,
          };

        case "paid_media":
          return {
            key: "/paid-media",
            label: "Paid Media",
            onClick: () => navigate("/paid-media"),
            ...commonProps,
          };

        case "klaviyo":
          return {
            key: "/klaviyo-acquisition",
            label: "Klaviyo Acquisition",
            onClick: () => navigate("/klaviyo-acquisition"),
            ...commonProps,
          };

        case "bulk_upload":
          return {
            key: "/bulk-upload",
            label: "Bulk Upload",
            onClick: () => navigate("/bulk-upload"),
            ...commonProps,
          };

        case "bulk_uploader":
          return {
            key: "/bulk-uploader",
            label: "Meta Bulk Uploader",
            onClick: () => navigate("/bulk-uploader"),
            ...commonProps,
            icon: <FacebookOutlined />,
          };

        case "whitelisted_media":
          return {
            key: "/whitelisted-media-comparison",
            label: "Whitelisted Media Comparison",
            onClick: () => navigate("/whitelisted-media-comparison"),
            ...commonProps,
          };

        case "budget_planner":
          return {
            key: "/target-settings",
            label: "Target Settings",
            onClick: () => navigate("/target-settings"),
            ...commonProps,
          };

        case "overview":
          return {
            key: "/performance/overview",
            label: !collapsed ? "Overview" : "",
            icon: (
              <div style={{ marginRight: "1px" }}>
                <CustomIcons icon="overview" height={20} width={20} />
              </div>
            ),
            title: !collapsed && "Overview",
            onClick: () => navigate("/performance/overview"),
            ...commonProps,
            style: { ...baseStyles, alignContent: "center" },
          };

        case "new_marketing_reporting": {
          // Get marketing reporting items
          const marketingReportingItems = [
            "google_ads",
            "facebook_marketing",
            "tiktok_advertiser_name",
            "snapchat_ads",
            "bing_ads",
            "pinterest_ads",
            "reddit_ads",
          ]
            .map((tab) => getMenuItemForTab(tab))
            .filter(Boolean)
            .sort((a, b) => {
              const orderMap = { Google: 1, Meta: 2, TikTok: 3, Snapchat: 4, Bing: 5, Pinterest: 6, Reddit: 7 };
              return (orderMap[a.label] || 99) - (orderMap[b.label] || 99);
            });

          // Create "Other" dropdown for offline items if they exist
          const validOfflineItems = offlineItems?.filter(item => item && item.key && item.label) || [];
          const otherChannelsSubmenu = validOfflineItems.length > 0 ? [
            {
              key: "/performance/marketing-reporting/other-submenu",
              label: "Other",
              ...commonProps,
              style: {
                ...baseStyles,
                paddingLeft: token.subMenuOtherIndent,
              },
              children: validOfflineItems.map(item => ({
                ...item,
                style: {
                  ...item.style,
                  paddingLeft: token.subMenuIndent, // Same indentation as standard marketing items
                }
              }))
            }
          ] : [];

          return {
            key: "/performance/marketing-reporting/submenu",
            label: !collapsed ? "Marketing Reporting" : "",
            icon: (
              <div style={{ marginRight: "1px" }}>
                <CustomIcons icon="marketing" height={20} width={20} />
              </div>
            ),
            children: [
              {
                key: "/performance/marketing-reporting",
                label: "Overview",
                onClick: () => navigate("/performance/marketing-reporting"),
                disabled: !isAllowed && !isLoading,
                style: {
                  animation: "fadeIn 1s",
                  paddingLeft: token.subMenuIndent,
                },
              },
              ...marketingReportingItems,
              ...otherChannelsSubmenu,
            ],
          };
        }

        case "google_ads":
          return {
            key: "/performance/marketing-reporting/google",
            label: "Google",
            onClick: () => navigate("/performance/marketing-reporting/google"),
            ...commonProps,
            style: {
              ...baseStyles,
              paddingLeft: token.subMenuIndent,
            },
          };

        case "snapchat_ads":
          return {
            key: "/performance/marketing-reporting/snapchat",
            label: "Snapchat",
            onClick: () => navigate("/performance/marketing-reporting/snapchat"),
            ...commonProps,
            style: {
              ...baseStyles,
              paddingLeft: token.subMenuIndent,
            },
          };

        case "facebook_marketing":
          return {
            key: "/performance/marketing-reporting/facebook",
            label: "Meta",
            onClick: () =>
              navigate("/performance/marketing-reporting/facebook"),
            ...commonProps,
            style: {
              ...baseStyles,
              paddingLeft: token.subMenuIndent,
            },
          };

        case "bing_ads":
          return {
            key: "/performance/marketing-reporting/bing",
            label: "Bing",
            onClick: () => navigate("/performance/marketing-reporting/bing"),
            ...commonProps,
            style: {
              ...baseStyles,
              paddingLeft: token.subMenuIndent,
            },
          };

        case "tiktok_advertiser_name":
          return {
            key: "/performance/marketing-reporting/tiktok",
            label: "TikTok",
            onClick: () => navigate("/performance/marketing-reporting/tiktok"),
            ...commonProps,
            style: {
              ...baseStyles,
              paddingLeft: token.subMenuIndent,
            },
          };

        case "pinterest_ads":
          return {
            key: "/performance/marketing-reporting/pinterest",
            label: "Pinterest",
            onClick: () => navigate("/performance/marketing-reporting/pinterest"),
            ...commonProps,
            style: {
              ...baseStyles,
              paddingLeft: token.subMenuIndent,
            },
          };

        case "reddit_ads":
          return {
            key: "/performance/marketing-reporting/reddit",
            label: "Reddit",
            onClick: () => navigate("/performance/marketing-reporting/reddit"),
            ...commonProps,
            style: {
              ...baseStyles,
              paddingLeft: token.subMenuIndent,
            },
          };

        case "influencer_media":
          return {
            key: "/influencer/content",
            label: "Content",
            icon: <FileDoneOutlined />,
            onClick: () => navigate("/influencer/content"),
            ...commonProps,
          };

        case "creative_summary":
          return {
            key: "/influencer/creative-summary",
            label: "Creative Summary",
            icon: <HighlightOutlined />,
            onClick: () => navigate("/influencer/creative-summary"),
            ...commonProps,
          };

        case "site_analytics_new":
          return {
            key: "/performance/site-analytics",
            label: "Site Analytics",
            icon: (
              <div style={{ marginRight: "1px" }}>
                <CustomIcons icon="site_analytics" height={20} width={20} />
              </div>
            ),
            onClick: () => navigate("/performance/site-analytics"),
            ...commonProps,
          };

        case "outreach_summary":
          return {
            key: "/influencer/outreach-summary",
            label: "Outreach Summary",
            icon: <SoundOutlined />,
            onClick: () => navigate("/influencer/outreach-summary"),
            ...commonProps,
          };

        case "legacy_settings":
          return {
            key: "/legacy_settings",
            label: "Settings (Legacy)",
            onClick: () => navigate("/legacy_settings"),
            // Settings is always enabled
            style: baseStyles,
            icon: <HistoryOutlined />,
            ...commonProps,
          };

        case "product_performance":
          return {
            key: "/performance/product-performance",
            label: "Product Performance",
            onClick: () => navigate("/performance/product-performance"),
            icon: (
              <div style={{ marginRight: "1px", marginTop: "5px" }}>
                <CustomIcons icon="product_performance" height={20} width={20} />
              </div>
            ),
            ...commonProps,
          };

        case "ai_health":
          return {
            key: "/seo/ai-health",
            label: "AI Health",
            onClick: () => navigate("/seo/ai-health"),
            icon: <RobotOutlined />,
            ...commonProps,
          };

        case "page_content_settings":
          return {
            key: "/settings/page-content",
            label: "Page Content Settings",
            onClick: () => navigate("/settings/page-content"),
            icon: <LayoutOutlined />,
            ...commonProps,
          };

        case "product_grouping_settings":
          return {
            key: "/settings/product-grouping",
            label: "Product Grouping Settings",
            onClick: () => navigate("/settings/product-grouping"),
            icon: <ShopOutlined />,
            ...commonProps,
          };

        case "budget_planner_settings":
          return {
            key: "/settings/target-settings",
            label: "Target Settings",
            onClick: () => navigate("/settings/target-settings"),
            icon: <DollarOutlined />,
            ...commonProps,
          };

        case "marketing_settings":
          return {
            key: "/settings/marketing",
            label: "Marketing Settings",
            onClick: () => navigate("/settings/marketing"),
            icon: <BarChartOutlined />,
            ...commonProps,
          };

        case "extra_costs_settings":
          return {
            key: "/settings/extra-costs",
            label: "Extra Costs Settings",
            onClick: () => navigate("/settings/extra-costs"),
            icon: <MoneyCollectOutlined />,
            ...commonProps,
          };

        case "campaign_mappings_settings":
          return {
            key: "/settings/campaign-mappings",
            label: "Campaign Mappings",
            onClick: () => navigate("/settings/campaign-mappings"),
            icon: <BranchesOutlined />,
            ...commonProps,
          };

        case "compass_multiplier_settings":
          return {
            key: "/settings/compass-multipliers",
            label: "Compass Multipliers Settings",
            onClick: () => navigate("/settings/compass-multipliers"),
            icon: <CalculatorOutlined />,
            ...commonProps,
          };

        case "admin_settings":
          return {
            key: "/settings/admin",
            label: "Admin Settings",
            onClick: () => navigate("/settings/admin"),
            icon: <UserOutlined />,
            disabled: !isAdmin && !isLoading,
            style: { animation: "fadeIn 1s" },
          };

        default:
          return null;
      }
    },
    [
      collapsed,
      navigate,
      token.subMenuIndent,
      allowedTabsSet,
      isLoading,
      offlineItems,
      isAdmin,
    ]
  );

  // Create skeleton menu items
  const MenuItemSkeleton = useCallback(
    (submenu = true, currentUrlPath, subMenuKey, itemCount) => {
      if (submenu) {
        return {
          key: subMenuKey,
          icon: <Skeleton.Button block active size="small" />,
          children: Array(itemCount)
            .fill()
            .map((_, index) => ({
              key: `${subMenuKey}-child-${index}`,
              icon: <Skeleton.Button block active size="small" />,
              style: {
                backgroundColor: "var(--dark-green-color)",
              },
            })),
        };
      }

      return {
        key: currentUrlPath,
        icon: <Skeleton.Button block active size="small" />,
      };
    },
    []
  );

  // Create menu items based on path and loading state
  const menuItems = useMemo(() => {
    // Show empty menu on root path
    if (currentUrlPath === "/") {
      return [];
    }

    // Show skeleton loaders during loading
    if (isLoading) {
      if (currentUrlPath.includes("performance")) {
        return [
          MenuItemSkeleton(false, currentUrlPath, "Overview"),
          MenuItemSkeleton(
            true,
            currentUrlPath,
            "/performance/marketing-reporting",
            5
          ),
          MenuItemSkeleton(false, currentUrlPath, "Site Analytics"),
          MenuItemSkeleton(false, currentUrlPath, "Customer Analytics"),
        ];
      } else if (currentUrlPath.includes("influencer")) {
        return [
          MenuItemSkeleton(false, currentUrlPath, "Content"),
          MenuItemSkeleton(false, currentUrlPath, "Creative Summary"),
          MenuItemSkeleton(false, currentUrlPath, "Outreach Summary"),
        ];
      } else if (currentUrlPath.includes("bulk-uploader")) {
        return [MenuItemSkeleton(false, currentUrlPath, "Bulk Uploader")];
      } else {
        // Default menu structure
        return [
          MenuItemSkeleton(true, currentUrlPath, "growth-dashboards", 4),
          MenuItemSkeleton(true, currentUrlPath, "paid-media-dashboards", 3),
          MenuItemSkeleton(false, currentUrlPath, "/bulk-upload"),
          MenuItemSkeleton(false, currentUrlPath, "/compass"),
          MenuItemSkeleton(false, currentUrlPath, "/settings"),
        ];
      }
    }

    // Show real menu items after loading
    if (currentUrlPath.includes("performance")) {
      const processedItems = [
        getMenuItemForTab("overview"),
        getMenuItemForTab("new_marketing_reporting"),
        getMenuItemForTab("site_analytics_new"),
        getMenuItemForTab("customer_performance"),
        getMenuItemForTab("product_performance"),
      ].filter(Boolean);

      return processedItems;
    }

    if (currentUrlPath.includes("/seo")) {
      const seoItems = [getMenuItemForTab("ai_health")].filter(Boolean);
      return seoItems;
    }

    if (currentUrlPath.includes("influencer")) {
      const influencerMenuItems = [
        getMenuItemForTab("influencer_media"),
        getMenuItemForTab("creative_summary"),
        getMenuItemForTab("outreach_summary"),
      ]
        .filter(Boolean)
        .sort((a, b) => a.label.localeCompare(b.label));

      return influencerMenuItems;
    }

    if (currentUrlPath.includes("bulk-uploader")) {
      return [getMenuItemForTab("bulk_uploader")];
    }

    if (currentUrlPath.includes("compass-portal")) {
      return [getMenuItemForTab("compass_portal")].filter(Boolean);
    }

    if (currentUrlPath.includes("settings")) {
      const settingsMenu = [
        getMenuItemForTab("settings_landing"),
        getMenuItemForTab("page_content_settings"),
        getMenuItemForTab("product_grouping_settings"),
        getMenuItemForTab("budget_planner_settings"),
        getMenuItemForTab("marketing_settings"),
        getMenuItemForTab("extra_costs_settings"),
        getMenuItemForTab("campaign_mappings_settings"),
        getMenuItemForTab("compass_multiplier_settings"),
      ];
      if (isAdmin) {
        settingsMenu.unshift(getMenuItemForTab("admin_settings"));
      }
      settingsMenu.push(getMenuItemForTab("legacy_settings"));
      return settingsMenu;
    }

    // Default menu structure for other paths
    const mainMenuItems = [];

    // Growth Dashboards submenu
    const dashboardItems = [
      "executive_summary",
      "site_analytics",
      "ltr",
      "klaviyo",
    ]
      .map((tab) => getMenuItemForTab(tab))
      .filter(Boolean);

    if (dashboardItems.length > 0) {
      mainMenuItems.push({
        key: "growth-dashboards",
        label: "Growth Dashboards",
        style: { animation: "fadeIn 1s" },
        children: dashboardItems,
      });
    }

    // Add Bulk Uploader as direct menu item
    const bulkUploaderItem = getMenuItemForTab("bulk_uploader");
    if (bulkUploaderItem) {
      mainMenuItems.push(bulkUploaderItem);
    }

    // Paid Media Dashboards submenu
    const mediaItems = ["paid_media", "whitelisted_media", "budget_planner"]
      .map((tab) => getMenuItemForTab(tab))
      .filter(Boolean);

    if (mediaItems.length > 0) {
      mainMenuItems.push({
        key: "paid-media-dashboards",
        label: "Paid Media Dashboards",
        style: { animation: "fadeIn 1s" },
        children: mediaItems,
      });
    }

    // Add inline tabs
    const inlineItems = ["compass", "compass_portal"]
      .map((tab) => getMenuItemForTab(tab))
      .filter(Boolean);

    if (inlineItems.length > 0) {
      mainMenuItems.push(...inlineItems);
    }

    // Settings item (always available)
    mainMenuItems.push(getMenuItemForTab("settings"));

    return mainMenuItems;
  }, [
    currentUrlPath,
    isLoading,
    getMenuItemForTab,
    MenuItemSkeleton,
    offlineItems,
    isAdmin,
  ]);

  return { menuItems };
}
